/**
 * DayClosePage — B1.10 DayCloseWizard smart wrapper (Faz 1.3).
 *
 * Architecture (`.cursor/rules/lovable-boundary.mdc`):
 *   - This page owns data fetching (`useDayCloseSnapshot`), tenant scoping
 *     (`useCurrentBranch`), wizard mutation wiring (start / patch* / submit
 *     / reopen), idempotency key derivation, toast surfacing, and navigation.
 *   - The Lovable-delivered composite (`DayCloseWizard`) in
 *     `shared/ui/day-close/` owns all visuals, step routing, variance
 *     summary bar, numeric inputs, manager-override dialog and Z-report
 *     preview.
 *
 * State machine (ADR-110):
 *   draft → counting → reconciling → review → submitted → closed | failed
 *
 * @see docs/decisions/business/ADR-110-business-dayclose-reconciliation.md
 * @see docs/_context/PETOTA_BUSINESS_LOVABLE_BRIEF_B1_10_DAYCLOSE_WIZARD.md
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCurrentBranch } from '../shared/context/TenantContext';
import {
  useDayCloseSnapshot,
  usePatchCashCounts,
  usePatchDeposit,
  usePatchNonCashCounts,
  useReopenDayClose,
  useStartDayClose,
  useSubmitDayClose,
  buildIdempotencyKey,
  type DenominationCountEntry,
  type ManagerOverrideInput,
  type NonCashTenderCount,
} from '../features/day-close';
import {
  DayCloseWizard,
  enDayCloseLabels,
  trDayCloseLabels,
  type DayCloseSnapshot as LovableDayCloseSnapshot,
} from '../shared/ui/day-close';

const MOBILE_BREAKPOINT = 768;

function useIsMobileBlocked(): boolean {
  const [blocked, setBlocked] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : window.innerWidth < MOBILE_BREAKPOINT,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const listener = () => setBlocked(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, []);
  return blocked;
}

function useIsOffline(): boolean {
  const [offline, setOffline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? false : !navigator.onLine,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return offline;
}

export function DayClosePage(): JSX.Element {
  const { i18n } = useTranslation(['dayClose', 'common']);
  const locale: 'tr' | 'en' = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const navigate = useNavigate();
  const branch = useCurrentBranch();
  const isMobileBlocked = useIsMobileBlocked();
  const isOffline = useIsOffline();

  const start = useStartDayClose();
  const snapshot = useDayCloseSnapshot(start.data?.sessionId);
  const sessionId = start.data?.sessionId;

  const patchCash = usePatchCashCounts(sessionId);
  const patchNonCash = usePatchNonCashCounts(sessionId);
  const patchDeposit = usePatchDeposit(sessionId);
  const submit = useSubmitDayClose(sessionId);
  const reopen = useReopenDayClose(sessionId);

  const attemptRef = useRef(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!branch?.branchId || start.data || start.isPending) return;
    start.mutate({ branchId: branch.branchId });
  }, [branch?.branchId, start]);

  useEffect(() => {
    if (start.error) setErrorMessage(start.error.message);
  }, [start.error]);

  const current = snapshot.data ?? start.data;
  const labels = locale === 'tr' ? trDayCloseLabels : enDayCloseLabels;

  const lovableSnapshot = useMemo<LovableDayCloseSnapshot | null>(() => {
    if (!current) return null;
    return current as unknown as LovableDayCloseSnapshot;
  }, [current]);

  const handlePatchCash = useCallback(
    (counts: readonly DenominationCountEntry[]) => {
      if (!sessionId) return;
      patchCash.mutate({ sessionId, counts });
    },
    [patchCash, sessionId],
  );

  const handlePatchNonCash = useCallback(
    (tenders: readonly NonCashTenderCount[]) => {
      if (!sessionId) return;
      patchNonCash.mutate({ sessionId, tenders });
    },
    [patchNonCash, sessionId],
  );

  const handlePatchDeposit = useCallback(
    (closingFloatMinor: number, depositAmountMinor: number) => {
      if (!sessionId) return;
      patchDeposit.mutate({ sessionId, closingFloatMinor, depositAmountMinor });
    },
    [patchDeposit, sessionId],
  );

  const handleSubmit = useCallback(
    async (override: ManagerOverrideInput | null) => {
      if (!sessionId || !current) return;
      attemptRef.current += 1;
      const idempotencyKey = buildIdempotencyKey(
        current.branchId,
        current.businessDate,
        attemptRef.current,
      );
      try {
        await submit.mutateAsync({ sessionId, override, idempotencyKey });
        setErrorMessage(null);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : labels.errors.submitFailed;
        setErrorMessage(message);
      }
    },
    [current, labels.errors.submitFailed, sessionId, submit],
  );

  const handleReopen = useCallback(() => {
    if (!sessionId) return;
    reopen.mutate({
      sessionId,
      reason: 'manual_reopen',
      explanation: 'Owner requested reopen',
    });
  }, [reopen, sessionId]);

  const handleOpenPrerequisite = useCallback(
    (_key: string, route: string | null) => {
      if (route) navigate(route);
    },
    [navigate],
  );

  const handleBackToCashier = useCallback(() => {
    navigate('/cashier');
  }, [navigate]);

  const isBusy =
    patchCash.isPending ||
    patchNonCash.isPending ||
    patchDeposit.isPending ||
    submit.isPending;

  return (
    <div className="h-full" data-testid="day-close-page">
      {lovableSnapshot ? (
        <DayCloseWizard
          snapshot={lovableSnapshot}
          labels={labels}
          locale={locale}
          isBusy={isBusy}
          isOffline={isOffline}
          isMobileBlocked={isMobileBlocked}
          onPatchCashCounts={handlePatchCash}
          onPatchNonCashCounts={handlePatchNonCash}
          onPatchDeposit={handlePatchDeposit}
          onSubmit={handleSubmit}
          onOpenPrerequisite={handleOpenPrerequisite}
          onBackToCashier={handleBackToCashier}
          onReopen={handleReopen}
          isOwner={false}
          errorMessage={errorMessage}
          onRetry={() => {
            setErrorMessage(null);
            if (branch?.branchId) start.mutate({ branchId: branch.branchId });
          }}
          dataTestId="day-close-wizard"
        />
      ) : (
        <div className="flex items-center justify-center h-full bg-biz-bg text-sm text-biz-foreground-muted">
          {labels.cta.openWizard}…
        </div>
      )}
    </div>
  );
}

export default DayClosePage;
